import type { QuerySnapshot } from 'firebase/firestore'

export type ConsultationIdentity = {
  lifecycleStatus: string
  operationalStatus: string
}

export type ConsultationIdentityMap = Map<string, ConsultationIdentity>

function extractConsultationIdentity(data: Record<string, unknown>): ConsultationIdentity {
  return {
    lifecycleStatus: String(data.lifecycleStatus ?? ''),
    operationalStatus: String(data.operationalStatus ?? ''),
  }
}

/** True when any doc was added/removed or lifecycle/operational status changed vs lastSeen. */
export function consultationIdentityChanged(
  snap: QuerySnapshot,
  lastSeen: ConsultationIdentityMap,
): boolean {
  for (const docChange of snap.docChanges()) {
    if (docChange.type === 'added' || docChange.type === 'removed') {
      return true
    }
    if (docChange.type === 'modified') {
      const consultationId = docChange.doc.id
      const next = extractConsultationIdentity(docChange.doc.data() as Record<string, unknown>)
      const previous = lastSeen.get(consultationId)
      if (!previous) {
        return true
      }
      if (
        previous.lifecycleStatus !== next.lifecycleStatus ||
        previous.operationalStatus !== next.operationalStatus
      ) {
        return true
      }
    }
  }
  return false
}

/** Apply snapshot docChanges to lastSeen after reload scheduling decisions. */
export function syncLastSeenFromSnapshot(
  snap: QuerySnapshot,
  lastSeen: ConsultationIdentityMap,
): void {
  for (const docChange of snap.docChanges()) {
    if (docChange.type === 'removed') {
      lastSeen.delete(docChange.doc.id)
      continue
    }
    lastSeen.set(
      docChange.doc.id,
      extractConsultationIdentity(docChange.doc.data() as Record<string, unknown>),
    )
  }
}
